"use client";

import { Suspense, useMemo, type ComponentProps } from "react";
import { useSearchParams } from "next/navigation";
import BookingCard from "@/components/product-detail/BookingCard";

type BookingCardProps = ComponentProps<typeof BookingCard>;
type Props = Omit<
  BookingCardProps,
  "initialPickupDate" | "initialReturnDate" | "initialPickupBranch"
>;

/**
 * URL 쿼리(`?checkin`·`?checkout`·`?branch`)로 초기값을 채운 BookingCard.
 *
 * 상품 상세는 ISR(정적 렌더) 페이지라 useSearchParams 를 쓰면 가장 가까운 Suspense
 * 경계까지 클라이언트 렌더로 넘어간다. 예전처럼 ProductDetailClient 최상단에서 읽으면
 * 상세 본문 전체(= SEO 본문)가 서버 HTML 에서 빠지므로, 쿼리를 읽는 부분만 이 경계
 * 안으로 모았다. 서버 HTML 에는 쿼리 없는 BookingCard(fallback)가 들어가고, 하이드레이션
 * 때 쿼리를 반영한 카드로 바뀐다.
 */
export default function QueryAwareBookingCard(props: Props) {
  return (
    <Suspense fallback={<BookingCard {...props} />}>
      <BookingCardWithQuery {...props} />
    </Suspense>
  );
}

function BookingCardWithQuery(props: Props) {
  const searchParams = useSearchParams();
  const { product } = props;

  // 검색 페이지에서 전달된 날짜 파라미터
  const initialPickupDate = useMemo(() => {
    const checkin = searchParams.get("checkin");
    return checkin ? new Date(checkin + "T00:00:00") : undefined;
  }, [searchParams]);
  const initialReturnDate = useMemo(() => {
    const checkout = searchParams.get("checkout");
    return checkout ? new Date(checkout + "T00:00:00") : undefined;
  }, [searchParams]);
  // 모빌리티 지점 허브 딥링크 `?branch=<branchId>` (설계 §3-4) — 그 지점을 픽업
  // 지점으로 미리 골라 준다. 이 상품에 연결되지 않은 id 는 조용히 무시한다
  // (링크를 손으로 고쳐 없는 지점으로 장바구니에 담는 것을 막는다).
  const initialPickupBranch = useMemo(() => {
    const branchId = searchParams.get("branch");
    if (!branchId) return undefined;
    return product?.branches?.some((b) => b.id === branchId)
      ? branchId
      : undefined;
  }, [searchParams, product]);

  return (
    <BookingCard
      {...props}
      initialPickupDate={initialPickupDate}
      initialReturnDate={initialReturnDate}
      initialPickupBranch={initialPickupBranch}
    />
  );
}
